import {readAuthData, refreshAuthToken} from '../services/auth.service';
import {ResponseStatus} from "../models/api-response.model";
import {PaginationParams} from "../models/pagination.model";

export const buildPaginatedUrl = (baseUrl: string, params?: PaginationParams): string => {
    if (!params) return baseUrl;
    const searchParams = new URLSearchParams();
    if (params.pageNumber !== undefined) searchParams.set('pageNumber', params.pageNumber.toString());
    if (params.pageSize !== undefined) searchParams.set('pageSize', params.pageSize.toString());
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortDirection) searchParams.set('sortDirection', params.sortDirection);
    if (params.search) searchParams.set('search', params.search);
    if (params.statusFilter) searchParams.set('statusFilter', params.statusFilter);
    const separator = baseUrl.includes('?') ? '&' : '?';
    const query = searchParams.toString();
    return query ? `${baseUrl}${separator}${query}` : baseUrl;
};

export interface ApiRequestOptions extends RequestInit {
    headers?: Record<string, string>;
    suppressAuthError?: boolean; // Option to suppress auth error events
}

// Create a custom event for auth errors
export const AUTH_ERROR_EVENT = 'auth_error';

// Prevent dispatching multiple auth errors in quick succession
let authErrorDispatched = false;
export const dispatchAuthError = () => {
    if (authErrorDispatched) return;
    authErrorDispatched = true;
    window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT));
    // Reset after a short delay so future genuine errors can still be dispatched
    setTimeout(() => { authErrorDispatched = false; }, 3000);
};

// Token refresh mutex: serialize concurrent refresh attempts
let refreshPromise: Promise<any> | null = null;

const doTokenRefresh = async (): Promise<any> => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = refreshAuthToken().finally(() => { refreshPromise = null; });
    return refreshPromise;
};

const emptyResponse = (status: string = ResponseStatus.SUCCESS) =>
    new Response(JSON.stringify({ status, message: '', body: null }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
    });

/**
 * Enhanced fetch function that automatically includes Authorization header when user is authenticated
 * Also handles token expiration by attempting to refresh the token and retry the request
 */
export const apiRequest = async (url: string, options: ApiRequestOptions = {}): Promise<Response> => {
    const currentUser = readAuthData();
    // Prepare headers
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options.headers
    };

    // Add Authorization header if user is authenticated
    if (currentUser && currentUser.token) {
        headers['Authorization'] = `Bearer ${currentUser.token}`;
    }

    try {
        // Make the API call with enhanced headers
        let response = await fetch(url, {
            ...options,
            headers
        });
        if ((response.status === 401 || response.status === 403) && currentUser) {
            try {
                // Try to refresh the token (serialized via mutex)
                const refreshResponse = await doTokenRefresh();
                if (!refreshResponse || refreshResponse.status !== ResponseStatus.SUCCESS || !refreshResponse.body?.token) {
                    if (!options.suppressAuthError) {
                        dispatchAuthError();
                    }
                    return emptyResponse();
                }
                // Token refresh was successful - retry the original request with new token
                headers['Authorization'] = `Bearer ${refreshResponse.body.token}`;
                return fetch(url, {
                    ...options,
                    headers
                });
            } catch (refreshError) {
                if (!options.suppressAuthError) {
                    dispatchAuthError();
                }
                return emptyResponse();
            }
        }
        return response;
    } catch (error) {
        // For network errors, don't trigger auth error
        return emptyResponse(ResponseStatus.SERVER_ERROR);
    }
};

/**
 * Convenience method for GET requests
 */
export const apiGet = async (url: string, options: ApiRequestOptions = {}): Promise<Response> => {
    try {
        return await apiRequest(url, {...options, method: 'GET'});
    } catch (error) {
        console.error("GET request failed:", error);
        // Return empty successful response to prevent error displays
        return Promise.resolve(new Response(JSON.stringify({
            status: ResponseStatus.SERVER_ERROR,
            message: '',
            body: null
        }), {
            status: 200,
            headers: {'Content-Type': 'application/json'}
        }));
    }
};

/**
 * Convenience method for POST requests
 */
export const apiPost = async (url: string, body?: any, options: ApiRequestOptions = {}): Promise<Response> => {
    try {
        return await apiRequest(url, {
            ...options,
            method: 'POST',
            body: body ? JSON.stringify(body) : undefined
        });
    } catch (error) {
        console.error("POST request failed:", error);
        // Return empty successful response to prevent error displays
        return Promise.resolve(new Response(JSON.stringify({
            status: ResponseStatus.SERVER_ERROR,
            message: '',
            body: null
        }), {
            status: 200,
            headers: {'Content-Type': 'application/json'}
        }));
    }
};

/**
 * Convenience method for PUT requests
 */
export const apiPut = async (url: string, body?: any, options: ApiRequestOptions = {}): Promise<Response> => {
    try {
        return await apiRequest(url, {
            ...options,
            method: 'PUT',
            body: body ? JSON.stringify(body) : undefined
        });
    } catch (error) {
        console.error("PUT request failed:", error);
        return Promise.reject(error);
    }
};

/**
 * Convenience method for DELETE requests
 */
export const apiDelete = async (url: string, options: ApiRequestOptions = {}): Promise<Response> => {
    try {
        return await apiRequest(url, {...options, method: 'DELETE'});
    } catch (error) {
        console.error("DELETE request failed:", error);
        return Promise.reject(error);
    }
};

/**
 * Binary GET (blob/download) helper. Uses the same auth + token refresh path as apiRequest.
 */
export const apiDownload = async (url: string, options: ApiRequestOptions = {}): Promise<Response> => {
    return apiRequest(url, {...options, method: 'GET'});
};

/**
 * Extracts the file name from a Content-Disposition header (prefers the RFC 5987
 * filename* form when present), falling back to the provided default.
 */
export const extractFileName = (disposition: string | null, fallback: string): string => {
    if (!disposition) {
        return fallback;
    }
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plain = disposition.match(/filename="?([^";]+)"?/i);
    const raw = encoded ? encoded[1] : plain ? plain[1] : null;
    if (!raw) {
        return fallback;
    }
    try {
        return decodeURIComponent(raw);
    } catch {
        return raw;
    }
};
